/**
 * Publish readiness — the ONE authored rule set for "is this offering ready".
 *
 * A PURE function over a plain snapshot: no Prisma, no Node built-ins, no
 * imports at all. That is deliberate. Two runtimes call it:
 *
 *   - The API is the only runtime evaluator for a PERSISTED offering. It
 *     assembles the snapshot from repos it already calls plus the tenant's
 *     Stripe connection state and a tenant-scoped ServiceImage count, then
 *     feeds one result to the list DTO (count), the detail DTO (structured),
 *     and the publish / event-scheduling doors (409).
 *   - The pre-creation draft canvas has no persisted row to evaluate, so it
 *     runs THIS function over its draft buffer. That is reuse of the same
 *     authored rules, not a second implementation, and it is what keeps the
 *     pre-creation canvas and the post-creation page from drifting.
 *
 * Import it from the webapp by subpath (`@telify/shared/service-admin/publish-readiness`)
 * so the bundle never reaches the server-heavy root barrel.
 *
 * Contract notes that are easy to break and expensive to notice:
 *
 *   1. This returns the STABLE APPLICABLE REQUIREMENT SET, not just failures.
 *      A blockers-only return would be a defect: a requirement disappears from
 *      it the moment it is satisfied, so no consumer could render a stable
 *      "N of M" denominator or keep a completed row visible in its group.
 *   2. AT MOST ONE BLOCKER PER SECTION. Enforced structurally by giving each
 *      visible section exactly one requirement whose predicates are ordered and
 *      first-failure-wins, not by filtering a flat list afterwards. About
 *      missing both descriptions is ONE step, not two.
 *   3. Requirement order matches the reading order of the shipped view pages
 *      (About card above Setup, then the setup rows top to bottom), so
 *      `blockers[0]` always names the first row the admin actually sees.
 *
 * Copy rule: blocker messages are authored HERE and nowhere else, and none of
 * them may contain an em dash (standing Gabriel rule).
 */

// ============================================================================
// Vocabulary
// ============================================================================

/**
 * Sections that can carry a blocker. These are the editor section keys
 * (`sectionRegistry.tsx`), so a detail page maps a blocker to a card
 * mechanically. The non-blocking remainder (`preparation`, `notifications`,
 * `availability`) and the view-page-only `access` row never receive one.
 */
/**
 * Declared as const arrays, not bare unions, so the Zod DTO schemas can derive
 * their enums from the SAME list. A new section key or blocker code is one edit,
 * and the route response schema cannot drift out of sync with the evaluator.
 */
export const PUBLISH_READINESS_SECTION_KEYS = [
  'about',
  'location',
  'photos',
  'payment',
  'booking-rules',
  'structure',
  'registration',
  'forms',
] as const;

export type PublishReadinessSectionKey = (typeof PUBLISH_READINESS_SECTION_KEYS)[number];

export const PUBLISH_BLOCKER_CODES = [
  'NAME_MISSING',
  'ABOUT_INCOMPLETE',
  'SHORT_DESCRIPTION_MISSING',
  'FULL_DESCRIPTION_MISSING',
  'LOCATION_INCOMPLETE',
  'COVER_PHOTO_MISSING',
  'PAYMENT_METHOD_MISSING',
  'DEPOSIT_AMOUNT_INVALID',
  'PRICE_MISSING',
  'STRIPE_NOT_CONNECTED',
  'STRIPE_CHARGES_DISABLED',
  'DURATION_MISSING',
  'EVENT_STRUCTURE_INVALID',
  'EVENT_CAPACITY_INVALID',
  'FORMS_NOT_LIVE',
] as const;

export type PublishBlockerCode = (typeof PUBLISH_BLOCKER_CODES)[number];

export interface PublishBlocker {
  sectionKey: PublishReadinessSectionKey;
  code: PublishBlockerCode;
  /** User-facing fix instruction. Authored here, rendered verbatim. */
  message: string;
}

export interface PublishRequirement {
  /** Stable identity. One requirement per visible section (see contract note 2). */
  key: PublishReadinessSectionKey;
  sectionKey: PublishReadinessSectionKey;
  /** Short noun phrase for a requirement row ("Cover photo", "About"). */
  label: string;
  satisfied: boolean;
  /** Non-null exactly when `satisfied` is false. */
  blocker: PublishBlocker | null;
}

export interface PublishReadiness {
  /** The APPLICABLE set. Membership does not change as the admin completes work. */
  requirements: PublishRequirement[];
  /** The unsatisfied projection, in requirement order. */
  blockers: PublishBlocker[];
  isReady: boolean;
}

/**
 * Services go LIVE; event templates never do, so their gates fire where
 * occurrences are born and the copy says "schedule". One rule set, two
 * vocabularies.
 */
type ReadinessGoal = 'go-live' | 'schedule';

// ============================================================================
// Snapshot
// ============================================================================

/**
 * Everything the rules read. Plain data on purpose: the API builds it from
 * Prisma rows, the create canvas builds it from a draft buffer, and neither
 * shape leaks into the rules.
 */
export interface PublishReadinessSnapshot {
  /** Event templates run the event rule set (structure + capacity, no duration). */
  isEvent: boolean;

  // About
  name?: string | null;
  /** The "Short description" field (`Service.summary`). */
  summary?: string | null;
  /** The "Full description" field (`Service.description`). */
  description?: string | null;

  // Location
  isVirtual?: boolean | null;
  /**
   * The studio phones the client at the client's own number (service-by-phone):
   * the location is complete with nothing more.
   */
  isByPhone?: boolean | null;
  virtualPlatform?: string | null;
  /**
   * The template's meeting link (virtual-sessions-finish). A virtual offering
   * needs it to go live or schedule; a draft saves without it.
   */
  virtualLink?: string | null;
  locationName?: string | null;
  locationAddress?: string | null;
  /** Required for a physical offering since Gabriel's 2026-08-09 ruling. */
  locationCity?: string | null;

  /**
   * The COVER OF RECORD: `Service.customImage`, written by the Photos editor's
   * Cover section together with `focalPointX/Y/focalPointScale`, and rendered by
   * every client hero and catalog card.
   *
   * This is what the cover requirement gates on (ledger Decision 36 / register
   * D-02, addendum RB-01). It REVERSES the earlier reading recorded here, that
   * the cover was positional (lowest-sortOrder `ServiceImage`) and that
   * `customImage` did not satisfy the rule: an offering created that way passed
   * the gate with `customImage` null and shipped the client fallback gradient.
   */
  customImage?: string | null;

  /**
   * Number of `ServiceImage` rows, which are now CAROUSEL photos only. No rule
   * gates on it: the carousel is optional beside the cover. Kept on the snapshot
   * because every caller already has the count and a future carousel rule would
   * otherwise re-derive it.
   */
  galleryImageCount?: number | null;

  // Payment (config)
  price?: number | null;
  /**
   * The price of EVERY selectable duration option, when the offering sells them.
   *
   * Load-bearing for the price rule, not decoration. Once an offering has
   * duration options the client books an OPTION, never the bare `price` column,
   * and the Payment editor deliberately stops maintaining that column
   * (`PaymentSection.tsx`). Reading `price` alone therefore answered the wrong
   * question in both directions: a stale non-zero `price` passed the gate for an
   * offering whose every option was still 0, and a correctly-priced
   * option-selling offering could be blocked over a column nothing charges.
   *
   * Empty (or absent) means the offering has no options and `price` is the
   * sellable price.
   */
  durationOptionPrices?: readonly number[] | null;
  paymentRequirement?: string | null;
  acceptedPaymentMethods?: readonly string[] | null;
  depositType?: string | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
  /**
   * Payment STATE, not config: a `stripeAccountId` EXISTS on the tenant's
   * payment account. Onboarding STARTED, which is not the same as being able
   * to charge — see `stripeChargesEnabled`.
   */
  stripeConnected?: boolean | null;
  /**
   * Payment STATE: Stripe's own charges-enabled flag, synced onto
   * `TenantPaymentAccount.stripeChargesEnabled` by the account webhooks.
   *
   * This, not account existence, is what publish gates on (ruling D-07): a
   * half-onboarded account reads as connected but cannot take a cent, so
   * letting it go live sells a client a checkout that will fail. Same signal
   * the client hub already gates `cardAvailable` on.
   */
  stripeChargesEnabled?: boolean | null;

  // Structure
  /** Services only. */
  duration?: number | null;
  /** Events only. */
  eventStructureType?: string | null;
  eventDaysCount?: number | null;
  eventMultidayType?: string | null;
  eventDurationPerDayMinutes?: number | null;
  /** Events only: spots per occurrence. */
  maxConcurrentBookings?: number | null;

  /** Lifecycle status of every attached form. Empty means nothing attached. */
  attachedFormStatuses?: readonly string[] | null;
}

/**
 * The offering-shaped half of a snapshot: the columns as they come off a Service
 * row or a draft buffer. Structurally typed on purpose so Prisma rows satisfy it
 * without this module ever importing Prisma. `price` must already be a number
 * (callers converting a Decimal do that anyway for their DTOs).
 */
export interface PublishReadinessOffering {
  isSharedEvent?: boolean | null;
  name?: string | null;
  summary?: string | null;
  description?: string | null;
  isVirtual?: boolean | null;
  /** Select it wherever readiness is evaluated (service-by-phone). */
  isByPhone?: boolean | null;
  virtualPlatform?: string | null;
  /** Select it wherever readiness is evaluated (virtual-sessions-finish). */
  virtualLink?: string | null;
  locationName?: string | null;
  locationAddress?: string | null;
  locationCity?: string | null;
  /** The cover of record. Select it wherever readiness is evaluated. */
  customImage?: string | null;
  price?: number | null;
  /** Price of each duration option the offering sells. See the snapshot field. */
  durationOptionPrices?: readonly number[] | null;
  paymentRequirement?: string | null;
  acceptedPaymentMethods?: readonly string[] | null;
  depositType?: string | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
  duration?: number | null;
  eventStructureType?: string | null;
  eventDaysCount?: number | null;
  eventMultidayType?: string | null;
  eventDurationPerDayMinutes?: number | null;
  maxConcurrentBookings?: number | null;
}

/** The three things a Service row cannot tell you about itself. */
export interface PublishReadinessContext {
  /**
   * Tenant-scoped top-level ServiceImage count (the CAROUSEL). Never a nested
   * include. Informational since Decision 36: the cover rule reads
   * `offering.customImage`.
   */
  galleryImageCount: number;
  /** Lifecycle status of every attached form. */
  attachedFormStatuses: readonly string[];
  /** A Stripe account row exists on the tenant's payment account. */
  stripeConnected: boolean;
  /** The synced `TenantPaymentAccount.stripeChargesEnabled`. What publish gates on. */
  stripeChargesEnabled: boolean;
}

/**
 * Assemble a snapshot from an offering row plus the three satellite reads.
 * Lives here so every caller (detail DTO, list mapping, publish door, event
 * doors, draft canvas) builds it the same way and a new field is one edit.
 */
export function buildPublishReadinessSnapshot(
  offering: PublishReadinessOffering,
  context: PublishReadinessContext,
): PublishReadinessSnapshot {
  return {
    isEvent: offering.isSharedEvent === true,
    name: offering.name,
    summary: offering.summary,
    description: offering.description,
    isVirtual: offering.isVirtual,
    isByPhone: offering.isByPhone,
    virtualPlatform: offering.virtualPlatform,
    virtualLink: offering.virtualLink,
    locationName: offering.locationName,
    locationAddress: offering.locationAddress,
    locationCity: offering.locationCity,
    customImage: offering.customImage,
    galleryImageCount: context.galleryImageCount,
    price: offering.price,
    durationOptionPrices: offering.durationOptionPrices,
    paymentRequirement: offering.paymentRequirement,
    acceptedPaymentMethods: offering.acceptedPaymentMethods,
    depositType: offering.depositType,
    depositAmount: offering.depositAmount,
    depositPercentage: offering.depositPercentage,
    stripeConnected: context.stripeConnected,
    stripeChargesEnabled: context.stripeChargesEnabled,
    duration: offering.duration,
    eventStructureType: offering.eventStructureType,
    eventDaysCount: offering.eventDaysCount,
    eventMultidayType: offering.eventMultidayType,
    eventDurationPerDayMinutes: offering.eventDurationPerDayMinutes,
    maxConcurrentBookings: offering.maxConcurrentBookings,
    attachedFormStatuses: context.attachedFormStatuses,
  };
}

// ============================================================================
// Predicate helpers (shared with the editors' Tier-1 inline copy)
// ============================================================================

/** True when a required text field contains something besides whitespace. */
export const isRequiredTextPresent = (value: string | null | undefined): boolean =>
  Boolean(value && value.trim().length > 0);

/** About is complete only when all three storefront text fields are present. */
export function isAboutComplete(snapshot: Pick<PublishReadinessSnapshot, 'name' | 'summary' | 'description'>): boolean {
  return isRequiredTextPresent(snapshot.name)
    && isRequiredTextPresent(snapshot.summary)
    && isRequiredTextPresent(snapshot.description);
}

/** The storefront cover is the service's custom image. */
export function hasCoverPhoto(snapshot: Pick<PublishReadinessSnapshot, 'customImage'>): boolean {
  return isRequiredTextPresent(snapshot.customImage);
}

/**
 * Location is complete when a virtual offering names its platform AND holds its
 * meeting link (virtual-sessions-finish), or a physical one has a name, an
 * address AND a city (ruling D-11, extended by Gabriel 2026-08-09 / register
 * F-55). A name with no address cannot be navigated to, an address with no name
 * reads as a bare string on the client page, and a street address with no city
 * is ambiguous the moment a tenant has clients in more than one town, so no
 * part alone is a location. A by-phone offering is complete with nothing more:
 * the number to call is the client's (service-by-phone). This is the PUBLISH
 * predicate.
 */
export function isLocationComplete(snapshot: PublishReadinessSnapshot): boolean {
  if (snapshot.isByPhone) return true;
  if (!isLocationSaveValid(snapshot)) return false;
  return snapshot.isVirtual ? isRequiredTextPresent(snapshot.virtualLink) : true;
}

/**
 * The Tier 1 SAVE predicate for the Location section: a virtual offering needs
 * its platform, a physical one its name, address and city. A virtual draft with
 * a platform and no meeting link saves (Gabriel 2026-10-12, ruling 9); the
 * missing link is a publish blocker only.
 */
export function isLocationSaveValid(snapshot: PublishReadinessSnapshot): boolean {
  // service-by-phone: the number to call is the client's, so nothing more is asked.
  if (snapshot.isByPhone) return true;
  return snapshot.isVirtual
    ? isRequiredTextPresent(snapshot.virtualPlatform)
    : PHYSICAL_LOCATION_PARTS.every((part) => isRequiredTextPresent(snapshot[part.field]));
}

/**
 * The physical-location parts, in the order the Location editor asks for them,
 * paired with the noun phrase the blocker message uses. ONE list so the
 * predicate and the message can never disagree about what is required.
 */
const PHYSICAL_LOCATION_PARTS = [
  { field: 'locationName', phrase: 'a location name' },
  { field: 'locationAddress', phrase: 'a street address' },
  { field: 'locationCity', phrase: 'a city' },
] as const satisfies readonly { field: keyof PublishReadinessSnapshot; phrase: string }[];

/** "a city" · "a street address and a city" · "a location name, a street address, and a city" */
function joinPhrases(phrases: readonly string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? '';
  if (phrases.length === 2) return `${phrases[0]} and ${phrases[1]}`;
  return `${phrases.slice(0, -1).join(', ')}, and ${phrases[phrases.length - 1]}`;
}

/** True when the offering charges online and therefore needs a working payment rail. */
export function requiresOnlinePayment(paymentRequirement: string | null | undefined): boolean {
  return paymentRequirement === 'FULL_PAYMENT' || paymentRequirement === 'DEPOSIT';
}

/** True when the offering charges a price, whether online or at the session. */
export function requiresPrice(paymentRequirement: string | null | undefined): boolean {
  return paymentRequirement === 'FULL_PAYMENT'
    || paymentRequirement === 'DEPOSIT'
    || paymentRequirement === 'PAY_AT_SESSION';
}

/**
 * A DEPOSIT offering must name how much. FIXED needs a positive amount,
 * PERCENTAGE needs 1-100. Shared with the Payment editor's save blocker so the
 * inline copy and the publish blocker can never disagree.
 */
export function isDepositAmountValid(snapshot: {
  depositType?: string | null;
  depositAmount?: number | null;
  depositPercentage?: number | null;
}): boolean {
  if (snapshot.depositType === 'FIXED') return (snapshot.depositAmount ?? 0) > 0;
  if (snapshot.depositType === 'PERCENTAGE') {
    const pct = snapshot.depositPercentage ?? 0;
    return pct >= 1 && pct <= 100;
  }
  return false;
}

/**
 * Does the offering have a price a client can actually be charged?
 *
 * When it sells duration options, the OPTIONS are what a client picks and pays,
 * and every selectable one must carry a positive price: a single 0-priced option
 * is a bookable path that collects nothing. The `price` column is ignored in
 * that case on purpose, because the Payment editor stops maintaining it once
 * options exist (`PaymentSection.tsx`), so whatever it still holds is stale.
 *
 * With no options, `price` is the sellable price and the rule is unchanged.
 *
 * Exported so the Payment editor's inline copy can share the exact predicate.
 */
export function hasSellablePrice(snapshot: {
  price?: number | null;
  durationOptionPrices?: readonly number[] | null;
}): boolean {
  const options = snapshot.durationOptionPrices ?? [];
  if (options.length > 0) return options.every((price) => (price ?? 0) > 0);
  return (snapshot.price ?? 0) > 0;
}

/** Event structure is complete when its shape is fully described. */
export function isEventStructureComplete(snapshot: PublishReadinessSnapshot): boolean {
  if (snapshot.eventStructureType === 'MULTI_DAY') {
    if ((snapshot.eventDaysCount ?? 0) < 2) return false;
    if (!isRequiredTextPresent(snapshot.eventMultidayType)) return false;
    if (snapshot.eventMultidayType === 'DAILY') return (snapshot.eventDurationPerDayMinutes ?? 0) > 0;
    return true;
  }
  if (snapshot.eventStructureType === 'SINGLE_BLOCK') return (snapshot.duration ?? 0) > 0;
  return false;
}

// ============================================================================
// Rules
// ============================================================================

/** "to go live" for services, "to schedule" for event templates. */
const suffix = (goal: ReadinessGoal): string => (goal === 'schedule' ? 'to schedule' : 'to go live');

interface RequirementRule {
  key: PublishReadinessSectionKey;
  label: string;
  /** Whether this requirement exists for this offering at all. */
  applies: (s: PublishReadinessSnapshot) => boolean;
  /**
   * Ordered predicates, first failure wins. Returning null means satisfied.
   * The single return is what enforces one-blocker-per-section.
   */
  evaluate: (s: PublishReadinessSnapshot, goal: ReadinessGoal) => { code: PublishBlockerCode; message: string } | null;
}

const aboutRule: RequirementRule = {
  key: 'about',
  label: 'About',
  applies: () => true,
  evaluate: (s, goal) => {
    if (!isRequiredTextPresent(s.name)) return { code: 'NAME_MISSING', message: `Add a name ${suffix(goal)}` };
    const hasShort = isRequiredTextPresent(s.summary);
    const hasFull = isRequiredTextPresent(s.description);
    if (!hasShort && !hasFull) {
      return { code: 'ABOUT_INCOMPLETE', message: `Add a short description and full description ${suffix(goal)}` };
    }
    if (!hasShort) return { code: 'SHORT_DESCRIPTION_MISSING', message: `Add a short description ${suffix(goal)}` };
    if (!hasFull) return { code: 'FULL_DESCRIPTION_MISSING', message: `Add a full description ${suffix(goal)}` };
    return null;
  },
};

const locationRule: RequirementRule = {
  key: 'location',
  label: 'Location',
  applies: () => true,
  evaluate: (s, goal) => {
    if (isLocationComplete(s)) return null;
    if (s.isVirtual) {
      if (!isRequiredTextPresent(s.virtualPlatform)) {
        return { code: 'LOCATION_INCOMPLETE', message: `Choose a virtual platform ${suffix(goal)}` };
      }
      return { code: 'LOCATION_INCOMPLETE', message: `Add the meeting link ${suffix(goal)}` };
    }
    // Name the parts that are actually missing, the way the About rule does.
    // Enumerated from the same list the predicate reads, so adding a required
    // part cannot leave the message describing the old rule.
    const missing = PHYSICAL_LOCATION_PARTS
      .filter((part) => !isRequiredTextPresent(s[part.field]))
      .map((part) => part.phrase);
    return {
      code: 'LOCATION_INCOMPLETE',
      message: `Add ${joinPhrases(missing)} ${suffix(goal)}`,
    };
  },
};

/**
 * The cover is `Service.customImage`, not a carousel row (Decision 36 / D-02).
 * Carousel photos are optional decoration; the cover is the storefront face the
 * client hero and every catalog card render.
 */
const photosRule: RequirementRule = {
  key: 'photos',
  label: 'Cover photo',
  applies: () => true,
  evaluate: (s, goal) =>
    hasCoverPhoto(s) ? null : { code: 'COVER_PHOTO_MISSING', message: `Add a cover photo ${suffix(goal)}` },
};

/**
 * Payment is the one requirement that reads STATE as well as config. Config
 * failures come first (the admin can fix them in the editor); Stripe state is
 * environmental and is a publish blocker only, never a save blocker.
 *
 * ALWAYS APPLICABLE (ruling F-14 / addendum RB-07). How an offering collects is
 * a decision every offering makes, including the ones that collect nothing, so
 * the row stays in "Needed to go live" and the denominator stays put. Scoping
 * `applies` to the online modes made the requirement VANISH the moment an admin
 * chose Free or Pay at the session, which both dropped the completed row out of
 * the required group and shrank the "N of M" count underfoot. Offline modes are
 * satisfied here rather than absent.
 */
const paymentRule: RequirementRule = {
  key: 'payment',
  label: 'Payment',
  applies: () => true,
  evaluate: (s) => {
    const online = requiresOnlinePayment(s.paymentRequirement);
    if (online && s.paymentRequirement === 'FULL_PAYMENT' && !(s.acceptedPaymentMethods ?? []).includes('CARD')) {
      return { code: 'PAYMENT_METHOD_MISSING', message: 'Turn on Card to accept payments' };
    }
    if (online && s.paymentRequirement === 'DEPOSIT' && !isDepositAmountValid(s)) {
      return { code: 'DEPOSIT_AMOUNT_INVALID', message: 'Set a deposit amount to accept payments' };
    }
    // EVENTS ONLY. A service authors its prices with its durations now, so its
    // missing-price blocker belongs to `booking-rules` — a blocker flags the
    // section where the fix happens. Event pricing is still authored in the
    // event Payment editor, so it stays here (durations-pricing-stepper).
    if (s.isEvent && requiresPrice(s.paymentRequirement) && !hasSellablePrice(s)) {
      return { code: 'PRICE_MISSING', message: 'Set a price to accept payments' };
    }
    // Free and pay-at-session need no online rail, method or Stripe account.
    if (!online) return null;
    if (!s.stripeConnected) {
      return { code: 'STRIPE_NOT_CONNECTED', message: 'Connect Stripe to accept card payments' };
    }
    if (!s.stripeChargesEnabled) {
      return {
        code: 'STRIPE_CHARGES_DISABLED',
        message: 'Finish your Stripe setup so your account can accept card payments',
      };
    }
    return null;
  },
};

/**
 * Services: how long the session runs AND what it costs. Both are authored in
 * the "Booking & pricing" editor, which is the only door to either
 * (durations-pricing-stepper), so both blockers flag it.
 *
 * The label is the DOOR's name (Gabriel ruling 2026-08-13), not the sub-heading
 * inside it, so a blocker names the row the admin taps. The `booking-rules`
 * section key is an internal identifier and stays as it is.
 *
 * Ordered predicates, first failure wins: a missing duration is reported before
 * a missing price, because an offering with no length has no option to price.
 */
const durationRule: RequirementRule = {
  key: 'booking-rules',
  label: 'Booking & pricing',
  applies: (s) => !s.isEvent,
  evaluate: (s, goal) => {
    if ((s.duration ?? 0) <= 0) return { code: 'DURATION_MISSING', message: `Set a duration ${suffix(goal)}` };
    if (requiresPrice(s.paymentRequirement) && !hasSellablePrice(s)) {
      return { code: 'PRICE_MISSING', message: 'Set a price to accept payments' };
    }
    return null;
  },
};

/** Events: structure replaces duration. */
const structureRule: RequirementRule = {
  key: 'structure',
  label: 'Structure',
  applies: (s) => s.isEvent,
  evaluate: (s, goal) =>
    isEventStructureComplete(s)
      ? null
      : { code: 'EVENT_STRUCTURE_INVALID', message: `Finish the event structure ${suffix(goal)}` },
};

/**
 * Events: capacity. Its own requirement rather than a second structure
 * predicate, because capacity is edited in the Registration section and a
 * blocker must flag the section where the fix happens.
 */
const capacityRule: RequirementRule = {
  key: 'registration',
  label: 'Registration',
  applies: (s) => s.isEvent,
  evaluate: (s, goal) =>
    (s.maxConcurrentBookings ?? 0) >= 1
      ? null
      : { code: 'EVENT_CAPACITY_INVALID', message: `Set how many spots are available ${suffix(goal)}` },
};

/**
 * Attached forms must be ACTIVE. Always applicable (vacuously satisfied with
 * nothing attached) so the set stays maximally stable. Surfaces as an amber
 * card flag on the post-creation detail page; the pre-creation canvas
 * deliberately keeps it silent by filtering on `key` (ledger Decision 35).
 *
 * RETAINED AS DEFENSE IN DEPTH (2026-08-12). Gabriel's ruling that day made a
 * non-ACTIVE attachment unreachable through the API from both ends: attach is
 * ACTIVE-only at all three doors (`linkFormToOffering`, `saveOfferingAssignments`,
 * and the offering create/PATCH validator via `findAttachableForms`), and
 * archiving a form DETACHES it from every offering and follow-up in the same
 * transaction as the status flip. This rule should therefore never fire in
 * practice. It stays anyway: it is the only thing that catches direct database
 * manipulation, a restored-from-backup row, or a future attach door that forgets
 * the guard. Do not delete it because it looks dead — that is the point.
 */
const formsRule: RequirementRule = {
  key: 'forms',
  label: 'Forms',
  applies: () => true,
  evaluate: (s, goal) => {
    const statuses = s.attachedFormStatuses ?? [];
    if (statuses.every((status) => status === 'ACTIVE')) return null;
    return { code: 'FORMS_NOT_LIVE', message: `Activate the attached forms ${suffix(goal)}` };
  },
};

/**
 * Reading order of the shipped view pages, which is what makes `blockers[0]`
 * name the first row the admin sees:
 *   services: About card, then Location, Photos, Payment, Booking rules, Forms
 *   events:   About card, then Location, Photos, Structure, Registration, Payment, Forms
 * The `applies` predicates pick the right subset, so one ordered list serves both.
 */
const RULES: readonly RequirementRule[] = [
  aboutRule,
  locationRule,
  photosRule,
  structureRule,
  capacityRule,
  paymentRule,
  durationRule,
  formsRule,
];

// ============================================================================
// Evaluator
// ============================================================================

/**
 * Evaluate an offering's publish readiness.
 *
 * Availability is deliberately absent from the required set: business-hours
 * defaults are a real schedule, so an untouched Availability section never
 * gates publishing (Gabriel 2026-08-04, ledger Decision 35 — supersedes the
 * July required-set item). Save-validity still refuses a custom-mode selection
 * saved empty, which is a rule about a self-contradictory save rather than a
 * publish requirement.
 */
export function evaluatePublishReadiness(snapshot: PublishReadinessSnapshot): PublishReadiness {
  const goal: ReadinessGoal = snapshot.isEvent ? 'schedule' : 'go-live';

  const requirements: PublishRequirement[] = [];
  for (const rule of RULES) {
    if (!rule.applies(snapshot)) continue;
    const failure = rule.evaluate(snapshot, goal);
    requirements.push({
      key: rule.key,
      sectionKey: rule.key,
      label: rule.label,
      satisfied: failure === null,
      blocker: failure === null ? null : { sectionKey: rule.key, code: failure.code, message: failure.message },
    });
  }

  const blockers = requirements
    .map((requirement) => requirement.blocker)
    .filter((blocker): blocker is PublishBlocker => blocker !== null);

  return { requirements, blockers, isReady: blockers.length === 0 };
}

/**
 * Headline for the readiness banner and the 409 message. Singular/plural
 * handled here so every surface says it the same way.
 */
export function formatPublishBlockerSummary(blockerCount: number, isEvent: boolean): string {
  if (blockerCount === 0) return isEvent ? 'Ready to schedule.' : 'Ready to publish.';
  const steps = blockerCount === 1 ? '1 step' : `${blockerCount} steps`;
  return isEvent
    ? `${steps} before an event can be scheduled from this template.`
    : `${steps} before this can go live.`;
}
